import React, { useState, useRef, useEffect, useCallback } from 'react';

const AutoExpandingTextbox = ({
  value = '',
  onChange,
  placeholder = 'Start typing...',
  maxHeight = 300,
  minHeight = 80,
  maxChars = null,
  label = '',
  className = '',
  disabled = false,
  required = false,
  name = '',
  id = '',
  onFocus = null,
  onBlur = null,
  updateKey = null,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const [isScrollable, setIsScrollable] = useState(false);
  const textareaRef = useRef(null);
  const isControlled = value !== undefined && onChange !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = `${minHeight}px`;
    
    const scrollHeight = textarea.scrollHeight;
    
    let newHeight = Math.max(scrollHeight, minHeight);
    
    if (newHeight >= maxHeight) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = 'auto';
      setIsScrollable(true);
    } else {
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = 'hidden';
      setIsScrollable(false);
    }
  }, [maxHeight, minHeight]);

  useEffect(() => {adjustHeight()}, [updateKey])

  const handleChange = (e) => {
    const newValue = e.target.value;
    
    if (maxChars && newValue.length > maxChars) {
      return;
    }

    if (isControlled) {
      onChange(e);
    } else {
      setInternalValue(newValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Backspace' || e.key === 'Delete') {
      setTimeout(() => adjustHeight(), 0);
    }
  };

  const handlePaste = () => {
    setTimeout(() => adjustHeight(), 0);
  };

  useEffect(() => {
    adjustHeight();
  }, [currentValue, adjustHeight]);

  useEffect(() => {
    adjustHeight();
  }, [adjustHeight]);

  const textareaId = id || `auto-expanding-${Math.random().toString(36).substr(2, 9)}`;

  const baseStyles = {
    minHeight: `${minHeight}px`,
    maxHeight: `${maxHeight}px`,
    resize: 'none',
    overflowY: 'hidden',
  };

  const scrollableStyles = {
    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.075)',
  };

  const combinedStyles = {
    ...baseStyles,
    ...(isScrollable ? scrollableStyles : {}),
  };

  return (
    <div className={`auto-expanding-textbox-container ${className}`}>
      {label && (
        <label 
          htmlFor={textareaId}
          style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: '600',
            color: '#333',
          }}
        >
          {label}
          {required && <span style={{ color: '#dc3545' }}>*</span>}
        </label>
      )}
      
      <textarea
        ref={textareaRef}
        id={textareaId}
        name={name}
        value={currentValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        style={combinedStyles}
        className={`form-control auto-expanding-textbox ${isScrollable ? 'scrollable' : ''}`}
        {...props}
      />

      <style jsx>{`
        .auto-expanding-textbox.scrollable::-webkit-scrollbar {
          width: 6px;
        }

        .auto-expanding-textbox.scrollable::-webkit-scrollbar-track {
          background: #f8f9fa;
          border-radius: 0.375rem;
          margin: 0.75rem 0;
        }

        .auto-expanding-textbox.scrollable::-webkit-scrollbar-thumb {
          background: #dee2e6;
          border-radius: 0.375rem;
          background-clip: content-box;
        }

        .auto-expanding-textbox.scrollable::-webkit-scrollbar-thumb:hover {
          background: #adb5bd;
          background-clip: content-box;
        }
      `}</style>
    </div>
  );
};

export default AutoExpandingTextbox;